import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { UpgradeModal } from '../components/subscription';
import { UserRole, SubscriptionType } from '@spellion/shared';
import { useTranslation } from '../hooks/useTranslation';
import { emailChangeService } from '../services/emailChangeService';
import { accountDeletionService } from '../services/accountDeletionService';
import { PasswordInput } from '../components/common/PasswordInput';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * AccountPage Component
 *
 * User account management page showing profile, subscription, and settings.
 * Displays user information, subscription status, and usage statistics.
 *
 * Features:
 * - User profile information (email, subscription type, registration date)
 * - Usage statistics (total searches, remaining daily searches)
 * - Last login date
 * - Link to subscription management
 * - Link to change password
 * - Logout functionality
 *
 * @validates Requirements 12.3, 9.1, 9.6
 */
export function AccountPage() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const user = useStore((state) => state.user);
  const token = useStore((state) => state.token);
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const logout = useStore((state) => state.logout);
  const resetTheme = useStore((state) => state.resetTheme);
  const quota = useStore((state) => state.quota);
  const setQuota = useStore((state) => state.setQuota);
  const darkMode = useStore((state) => state.darkMode);
  const setDarkMode = useStore((state) => state.setDarkMode);
  const saveThemeToServer = useStore((state) => state.saveThemeToServer);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState<string | null>(null);
  const [pendingEmailChange, setPendingEmailChange] = useState<{ newEmail: string; expiresAt: string } | null>(null);
  const [cancellingEmailChange, setCancellingEmailChange] = useState(false);

  // Account deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isLoadingDeletion, setIsLoadingDeletion] = useState(false);
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const [deletionDelayDays, setDeletionDelayDays] = useState<number>(30);

  // Compute isPremium from user data
  const userIsPremium = user?.subscriptionType === SubscriptionType.PREMIUM || user?.role === UserRole.ADMIN;

  // Load quota on mount
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    
    const fetchQuota = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/subscription/quota`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.data?.quota) {
          setQuota(data.data.quota);
        }
      } catch (error) {
        console.error('Failed to load quota:', error);
      }
    };
    
    fetchQuota();
  }, [isAuthenticated, token, setQuota]);

  // Load pending email change on mount
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    
    const fetchPendingEmailChange = async () => {
      try {
        const response = await emailChangeService.getPendingEmailChange(token);
        if (response.success && response.data) {
          setPendingEmailChange(response.data);
        }
      } catch (error) {
        console.error('Failed to load pending email change:', error);
      }
    };
    
    fetchPendingEmailChange();
  }, [isAuthenticated, token]);

  // Load deletion delay on mount
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    
    const fetchDeletionDelay = async () => {
      try {
        console.log('[AccountPage] Fetching deletion delay...');
        const response = await accountDeletionService.getDeletionDelay(token);
        console.log('[AccountPage] Deletion delay response:', JSON.stringify(response));
        if (response.success && response.data) {
          console.log('[AccountPage] Setting deletion delay to:', response.data.delayDays);
          setDeletionDelayDays(response.data.delayDays);
        } else {
          console.error('[AccountPage] Failed to get deletion delay:', response.error);
        }
      } catch (error) {
        console.error('Failed to load deletion delay:', error);
      }
    };
    
    fetchDeletionDelay();
  }, [isAuthenticated, token]);

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <div className="max-w-md mx-auto py-8 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-4">
          {t('account.loginRequired.title')}
        </h1>
        <p className="text-slate-600 mb-6">
          {t('account.loginRequired.message')}
        </p>
        <Link to="/login" className="btn-primary">
          {t('account.loginRequired.login')}
        </Link>
      </div>
    );
  }

  /**
   * Format date for display
   */
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return t('account.info.notAvailable');
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(currentLanguage === 'fr' ? 'fr-FR' : 'en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    } catch {
      return t('account.info.notAvailable');
    }
  };

  /**
   * Get subscription type label
   */
  const getSubscriptionLabel = (): { label: string; className: string } => {
    if (userIsPremium) {
      return {
        label: t('account.accountTypes.premium'),
        className: 'bg-accent-100 dark:bg-accent-900/40 text-accent-700 dark:text-accent-300',
      };
    }
    return {
      label: t('account.accountTypes.free'),
      className: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
    };
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    // Reset theme to light mode FIRST before logout
    resetTheme();
    logout();
    navigate('/');
  };

  /**
   * Handle dark mode toggle
   */
  const handleDarkModeToggle = async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    if (token) {
      await saveThemeToServer(token, newValue);
    }
  };

  /**
   * Handle email change request submission
   */
  const handleEmailChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailChangeError(null);
    setEmailChangeSuccess(null);

    // Basic validation
    if (!newEmail.trim()) {
      setEmailChangeError(t('account.emailChange.errors.invalidEmail'));
      return;
    }

    if (newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      setEmailChangeError(t('account.emailChange.errors.sameEmail'));
      return;
    }

    if (!currentPassword.trim()) {
      setEmailChangeError(t('account.emailChange.errors.invalidPassword'));
      return;
    }

    if (!token) {
      console.error('[EmailChange] No token available');
      return;
    }

    console.log('[EmailChange] Submitting with token:', token ? 'present' : 'missing');
    setEmailChangeLoading(true);

    try {
      const response = await emailChangeService.requestEmailChange(token, newEmail, currentPassword);
      
      if (response.success) {
        setEmailChangeSuccess(t('account.emailChange.success'));
        setNewEmail('');
        setCurrentPassword('');
        // Reload pending change
        const pendingResponse = await emailChangeService.getPendingEmailChange(token);
        if (pendingResponse.success && pendingResponse.data) {
          setPendingEmailChange(pendingResponse.data);
        }
      } else {
        // Map error codes to translations
        const errorCode = response.error?.code;
        console.log('[EmailChange] Error code from server:', errorCode);
        let errorMessage = t('account.emailChange.errors.generic');
        
        if (errorCode === 'SAME_EMAIL') {
          errorMessage = t('account.emailChange.errors.sameEmail');
        } else if (errorCode === 'INVALID_PASSWORD') {
          errorMessage = t('account.emailChange.errors.invalidPassword');
        } else if (errorCode === 'EMAIL_ALREADY_EXISTS' || errorCode === 'EMAIL_EXISTS') {
          errorMessage = t('account.emailChange.errors.emailExists');
        } else if (errorCode === 'EMAIL_ALREADY_PENDING' || errorCode === 'EMAIL_PENDING') {
          errorMessage = t('account.emailChange.errors.emailPending');
        }
        
        setEmailChangeError(errorMessage);
      }
    } catch (error) {
      console.error('Email change error:', error);
      setEmailChangeError(t('account.emailChange.errors.generic'));
    } finally {
      setEmailChangeLoading(false);
    }
  };

  /**
   * Handle cancelling pending email change
   */
  const handleCancelEmailChange = async () => {
    if (!token) return;
    
    setCancellingEmailChange(true);
    setEmailChangeError(null);
    setEmailChangeSuccess(null);

    try {
      const response = await emailChangeService.cancelEmailChange(token);
      
      if (response.success) {
        setPendingEmailChange(null);
        setEmailChangeSuccess(t('account.emailChange.cancelSuccess'));
      } else {
        setEmailChangeError(t('account.emailChange.errors.generic'));
      }
    } catch (error) {
      console.error('Cancel email change error:', error);
      setEmailChangeError(t('account.emailChange.errors.generic'));
    } finally {
      setCancellingEmailChange(false);
    }
  };

  /**
   * Handle account deletion request
   */
  const handleRequestDeletion = async () => {
    if (!token) return;
    
    setIsLoadingDeletion(true);
    setDeletionError(null);

    try {
      const response = await accountDeletionService.requestDeletion(token);
      
      if (response.success) {
        // Account deletion requested - log the user out immediately
        resetTheme();
        logout();
        navigate('/');
      } else {
        setDeletionError(response.error?.message || t('account.deletion.errors.generic'));
      }
    } catch (error) {
      console.error('Failed to request deletion:', error);
      setDeletionError(t('account.deletion.errors.generic'));
    } finally {
      setIsLoadingDeletion(false);
    }
  };

  /**
   * Format date for pending email change expiration
   */
  const formatExpirationDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(currentLanguage === 'fr' ? 'fr-FR' : 'en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const subscriptionInfo = getSubscriptionLabel();

  // Calculate remaining searches for free users
  const remainingSearches = quota?.remaining ?? 0;

  return (
    <div className="max-w-4xl mx-auto py-8">
      <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-100 mb-8">
        {t('account.title')}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Section */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Info Card */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-4">
              {t('account.info.title')}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                  {t('account.info.email')}
                </label>
                <p className="text-slate-900 dark:text-slate-100">{user.email}</p>
              </div>

              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                  {t('account.info.memberSince')}
                </label>
                <p className="text-slate-900 dark:text-slate-100">{formatDate(user.createdAt)}</p>
              </div>

              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                  {t('account.info.accountType')}
                </label>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 text-sm rounded-full ${subscriptionInfo.className}`}
                >
                  {subscriptionInfo.label}
                </span>
              </div>
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-4">
              {t('account.stats.title')}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {quota?.used ?? 0}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('account.stats.searchesToday')}
                </p>
              </div>
              {userIsPremium ? (
                <div className="text-center p-4 bg-accent-50 dark:bg-accent-900/30 rounded-xl col-span-1 md:col-span-2">
                  <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">∞</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('account.stats.unlimited')}</p>
                </div>
              ) : (
                <>
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                      {remainingSearches}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('account.stats.remaining')}</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
                    <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                      {quota?.limit ?? 20}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('account.stats.dailyLimit')}</p>
                  </div>
                </>
              )}
            </div>

            {!userIsPremium && quota && (
              <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg text-sm text-primary-700 dark:text-primary-300">
                <p>
                  {t('account.stats.premiumPromo')}
                </p>
              </div>
            )}
          </div>

          {/* Security Section */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-4">
              {t('account.security.title')}
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{t('account.security.password')}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('account.security.passwordDescription')}
                  </p>
                </div>
                <Link
                  to="/password-reset-request"
                  className="btn-outline text-sm"
                >
                  {t('account.security.change')}
                </Link>
              </div>
            </div>
          </div>

          {/* Email Change Section */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-4">
              {t('account.emailChange.title')}
            </h2>

            {/* Current email display */}
            <div className="mb-4">
              <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                {t('account.emailChange.currentEmail')}
              </label>
              <p className="text-slate-900 dark:text-slate-100 font-medium">{user.email}</p>
            </div>

            {/* Success/Error messages */}
            {emailChangeSuccess && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm">
                {emailChangeSuccess}
              </div>
            )}
            {emailChangeError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {emailChangeError}
              </div>
            )}

            {/* Pending email change notice */}
            {pendingEmailChange ? (
              <div className="p-4 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg">
                <h3 className="font-medium text-primary-900 dark:text-primary-100 mb-2">
                  {t('account.emailChange.pendingTitle')}
                </h3>
                <p className="text-sm text-primary-700 dark:text-primary-300 mb-2">
                  {t('account.emailChange.pendingMessage').replace('{email}', pendingEmailChange.newEmail)}
                </p>
                <p className="text-xs text-primary-600 dark:text-primary-400 mb-3">
                  {t('account.emailChange.pendingExpires').replace('{date}', formatExpirationDate(pendingEmailChange.expiresAt))}
                </p>
                <button
                  type="button"
                  onClick={handleCancelEmailChange}
                  disabled={cancellingEmailChange}
                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium disabled:opacity-50"
                >
                  {cancellingEmailChange ? t('account.emailChange.cancelling') : t('account.emailChange.cancelPending')}
                </button>
              </div>
            ) : (
              /* Email change form */
              <form onSubmit={handleEmailChangeSubmit} className="space-y-4">
                <div>
                  <label htmlFor="newEmail" className="block text-sm text-slate-700 dark:text-slate-300 mb-1">
                    {t('account.emailChange.newEmail')}
                  </label>
                  <input
                    type="email"
                    id="newEmail"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder={t('account.emailChange.newEmailPlaceholder')}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={emailChangeLoading}
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label htmlFor="currentPassword" className="block text-sm text-slate-700 dark:text-slate-300 mb-1">
                    {t('account.emailChange.currentPassword')}
                  </label>
                  <PasswordInput
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('account.emailChange.currentPasswordPlaceholder')}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={emailChangeLoading}
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={emailChangeLoading || !newEmail.trim() || !currentPassword.trim()}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailChangeLoading ? t('account.emailChange.submitting') : t('account.emailChange.submit')}
                </button>
              </form>
            )}
          </div>

          {/* Appearance Section - Dark Mode */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-4">
              {t('account.appearance.title')}
            </h2>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {t('account.appearance.darkMode')}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('account.appearance.darkModeDescription')}
                </p>
              </div>
              <button
                type="button"
                onClick={handleDarkModeToggle}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                  ${darkMode ? 'bg-primary-600' : 'bg-slate-200 dark:bg-slate-600'}
                `}
                role="switch"
                aria-checked={darkMode}
                aria-label={t('account.appearance.darkMode')}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm
                    ${darkMode ? 'translate-x-6' : 'translate-x-1'}
                  `}
                />
              </button>
            </div>
          </div>

          {/* Account Deletion Section - Hidden for admin users */}
          {user.role !== UserRole.ADMIN && (
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-red-200 dark:border-red-800">
              <h2 className="font-semibold text-lg text-red-600 dark:text-red-400 mb-2">
                {t('account.deletion.title')}
              </h2>
              
              {showDeleteConfirm ? (
                <div className="space-y-4">
                  {/* Warning box */}
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-amber-800 dark:text-amber-300 font-medium mb-2">
                      {t('account.deletion.warningTitle')}
                    </p>
                    <ul className="list-disc list-inside text-amber-700 dark:text-amber-400 text-sm space-y-1">
                      <li>{t('account.deletion.warningImmediate')}</li>
                      <li>{t('account.deletion.warningExport')}</li>
                      <li>{t('account.deletion.warningData').replace('{days}', String(deletionDelayDays))}</li>
                      <li>{t('account.deletion.warningContact').replace('{days}', String(deletionDelayDays))}</li>
                    </ul>
                  </div>

                  {/* Confirmation input */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('account.deletion.confirmLabel')}
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={t('account.deletion.confirmPlaceholder')}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      disabled={isLoadingDeletion}
                    />
                  </div>

                  {/* Error message */}
                  {deletionError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                      {deletionError}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                        setDeletionError(null);
                      }}
                      disabled={isLoadingDeletion}
                      className="btn-outline"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRequestDeletion}
                      disabled={isLoadingDeletion || deleteConfirmText.toUpperCase() !== t('account.deletion.confirmWord').toUpperCase()}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingDeletion ? t('common.loading') : t('account.deletion.confirmButton')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    {t('account.deletion.description')}
                  </p>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                    >
                      {t('account.deletion.button')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Subscription */}
        <div className="space-y-6">
          {/* Subscription Card */}
          {!userIsPremium ? (
            <div className="bg-gradient-to-br from-primary-500 to-secondary-600 p-6 rounded-2xl text-white">
              <h2 className="font-semibold text-lg mb-2">{t('account.premium.upgrade.title')}</h2>
              <p className="text-primary-100 text-sm mb-4">
                {t('account.premium.upgrade.description')}
              </p>

              <ul className="space-y-2 text-sm mb-4">
                <li className="flex items-center gap-2">
                  <span className="text-primary-200">✓</span>
                  {t('account.premium.upgrade.benefits.unlimited')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary-200">✓</span>
                  {t('account.premium.upgrade.benefits.allDictionaries')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-primary-200">✓</span>
                  {t('account.premium.upgrade.benefits.import')}
                </li>
              </ul>

              <Link
                to="/subscription"
                className="block w-full text-center py-3 bg-white text-primary-600 font-medium rounded-xl hover:bg-primary-50 transition-colors"
              >
                {t('account.premium.upgrade.viewOffers')}
              </Link>
              <button
                type="button"
                onClick={() => setShowUpgradeModal(true)}
                className="block w-full text-center py-2 mt-2 text-primary-100 text-sm hover:text-white transition-colors"
              >
                {t('account.premium.upgrade.subscribeNow')}
              </button>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-accent-600 to-accent-700 dark:from-accent-700 dark:to-accent-800 p-6 rounded-2xl text-white shadow-lg">
              <h2 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <span>✨</span>
                {t('account.premium.active.title')}
              </h2>
              <p className="text-white text-sm mb-4">
                {t('account.premium.active.description')}
              </p>

              <ul className="space-y-2 text-sm mb-4 text-white">
                <li className="flex items-center gap-2">
                  <span className="text-white font-bold">✓</span>
                  {t('account.premium.upgrade.benefits.unlimited')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-white font-bold">✓</span>
                  {t('account.premium.upgrade.benefits.allDictionaries')}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-white font-bold">✓</span>
                  {t('account.premium.upgrade.benefits.import')}
                </li>
              </ul>

              <Link
                to="/subscription"
                className="block w-full text-center py-3 bg-white text-accent-700 font-medium rounded-xl hover:bg-accent-50 transition-colors"
              >
                {t('account.premium.active.manage')}
              </Link>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-4">
              {t('account.quickActions.title')}
            </h2>

            <div className="space-y-2">
              <Link
                to="/"
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span>🔍</span>
                <span className="text-slate-700 dark:text-slate-300">{t('account.quickActions.newSearch')}</span>
              </Link>
              <Link
                to="/subscription"
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span>💳</span>
                <span className="text-slate-700 dark:text-slate-300">{t('account.quickActions.manageSubscription')}</span>
              </Link>
              <Link
                to="/password-reset-request"
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span>🔒</span>
                <span className="text-slate-700 dark:text-slate-300">{t('account.quickActions.changePassword')}</span>
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-2 w-full p-3 rounded-lg text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-red-600 dark:text-red-400"
              >
                <span>🚪</span>
                <span>{t('account.quickActions.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSuccess={() => {
          // Refresh page to show updated subscription status
          window.location.reload();
        }}
      />
    </div>
  );
}
